import { format, subDays } from 'date-fns';
import { useLazyGetScommesseQuery } from 'features/api/userSlice';
import { selectIsOpenCarrello, selectStato, selectTab, selectTipo } from 'features/carrello/carrelloSelectors';
import { statoCarrelloSelected, tipoCarrelloSelected } from 'features/carrello/carrelloSlice';
import { CarrelloTabEnum } from 'features/carrello/types';
import { useInternalSession } from 'hooks';
import { useTranslation } from 'hooks/useLingUI';
import { ScommesseResponseApiItem } from 'lib/api/scommesseResponseApi';
import { DatoMenuItemBlock } from 'lib/datoCms/types';
import Persist, { StorageKind } from 'lib/persist';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyGetMovimentiHappybetQuery } from 'sites/happybet/features/api/happybetApiSlice';
import {
  MovimentiDirezioneEnum,
  MovimentiHbetMovimentoResDto,
  MovimentiStatoScommessaEnum,
  MovimentiTipologiaEnum,
  MovimentiTipoScommessaEnum,
} from 'types/swagger';
import { isClientSide, isHappybetSite } from 'utility/constant';
import { isPersonalUrl, isTruthy } from 'utility/functions';

const storage = Persist(StorageKind.session);
const uid = 'bet-polling-ec0f00b5-3443-42c4-a48e-4753f2fcc228';

export interface TipoScommessaHookType {
  tipoScommessa: MovimentiTipoScommessaEnum;
  tipoScommessaIndex: number;
  tabsTipoScommessa: Array<DatoMenuItemBlock>;
  setTipoScommessa: (_: MovimentiTipoScommessaEnum) => void;
  setTipoScommessaByIndex: (_: number) => void;
}
// tipo scommessa / tipo carrello / verticale
// 1st level tabs for snai / unmanagen for hoybet since virtual and ippica are not implemented
export const useTipoScommessaTabs = (): TipoScommessaHookType => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const traceRef = useRef(false);
  useEffect(() => {
    if (isClientSide) {
      const search = new URLSearchParams(document.location.search.substring(1));
      const trace = search.get('trace') ?? '';
      traceRef.current = trace.toLowerCase().includes('bets');
    }
  }, []);

  const tipoScommessa = useSelector(selectTipo);
  const setTipoScommessa = async (nextTipo: MovimentiTipoScommessaEnum): Promise<void> => {
    if (traceRef.current) {
      console.log(`MyBets: useTipoScommessaTabs.setTipoScommessa "${tipoScommessa}" -> "${nextTipo}"`);
    }
    await dispatch(tipoCarrelloSelected(nextTipo));
  };

  const tabsTipoScommessa = useMemo(
    (): Array<DatoMenuItemBlock> => [
      {
        id: MovimentiTipoScommessaEnum.Sport,
        label: t('sport', MovimentiTipoScommessaEnum.Sport),
        linkBlock: [{ id: MovimentiTipoScommessaEnum.Sport, href: '' }],
      },
      {
        id: MovimentiTipoScommessaEnum.Ippica,
        label: t('ippica', MovimentiTipoScommessaEnum.Ippica),
        linkBlock: [{ id: MovimentiTipoScommessaEnum.Ippica, href: '' }],
      },
      {
        id: MovimentiTipoScommessaEnum.Virtual,
        label: t('virtual', MovimentiTipoScommessaEnum.Virtual),
        linkBlock: [{ id: MovimentiTipoScommessaEnum.Virtual, href: '' }],
      },
    ],
    [t]
  );

  const tipoScommessaIndex = useMemo(
    () => tabsTipoScommessa.findIndex((x) => x.id === tipoScommessa),
    [tabsTipoScommessa, tipoScommessa]
  );

  const setTipoScommessaByIndex = async (idx: number): Promise<void> => {
    const nextValue = tabsTipoScommessa?.[idx]?.id;
    if (traceRef.current) {
      console.log(`MyBets: useTipoScommessaTabs.setTipoScommessaByIndex(${idx}) -> "${nextValue}"`);
    }
    await setTipoScommessa(nextValue as MovimentiTipoScommessaEnum);
  };

  // if(traceRef.current){
  //   console.log(`MyBets: useTipoScommessaTabs tipoScommessa:${tipoScommessa}, tipoScommessaIndex:${tipoScommessaIndex}, tabsTipoScommessa:`, tabsTipoScommessa)
  // }

  return {
    tipoScommessa,
    tipoScommessaIndex,
    tabsTipoScommessa,
    setTipoScommessa,
    setTipoScommessaByIndex,
  };
};

export interface StatoScommessaHookType {
  statoScommessa: MovimentiStatoScommessaEnum;
  statoScommessaIndex: number;
  tabsStatoScommessa: Array<DatoMenuItemBlock>;
  setStatoScommessa: (_: MovimentiStatoScommessaEnum) => void;
  setStatoScommessaByIndex: (_: number) => void;
}
// stato scommessa
// 2nd level tabs - it drives the status filter
export const useStatoScommessaTabs = (): StatoScommessaHookType => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const traceRef = useRef(false);
  useEffect(() => {
    if (isClientSide) {
      const search = new URLSearchParams(document.location.search.substring(1));
      const trace = search.get('trace') ?? '';
      traceRef.current = trace.toLowerCase().includes('bets');
    }
  }, []);

  const tipoScommessa = useSelector(selectTipo);

  const statoScommessa = useSelector(selectStato);
  const setStatoScommessa = async (nextStato: MovimentiStatoScommessaEnum): Promise<void> => {
    if (traceRef.current) {
      console.log(`MyBets: useStatoScommessaTabs.setStatoScommessa ${statoScommessa} -> ${nextStato}`);
    }
    await dispatch(statoCarrelloSelected(nextStato));
  };

  const tabsStatoScommessa = useMemo((): Array<DatoMenuItemBlock> => {
    const objAperta: DatoMenuItemBlock = {
      id: MovimentiStatoScommessaEnum.Aperta,
      label: t('aperte', MovimentiStatoScommessaEnum.Aperta),
      linkBlock: [{ id: MovimentiStatoScommessaEnum.Aperta, href: '' }],
    };
    const objChiusa: DatoMenuItemBlock = {
      id: MovimentiStatoScommessaEnum.Chiusa,
      label: t('chiuse', MovimentiStatoScommessaEnum.Chiusa),
      linkBlock: [{ id: MovimentiStatoScommessaEnum.Chiusa, href: '' }],
    };

    const result = [objAperta, objChiusa];

    if (tipoScommessa === MovimentiTipoScommessaEnum.Sport) {
      const objLive: DatoMenuItemBlock = {
        id: MovimentiStatoScommessaEnum.Live,
        label: t('live', MovimentiStatoScommessaEnum.Live),
        linkBlock: [{ id: MovimentiStatoScommessaEnum.Live, href: '' }],
      };

      result.splice(1, 0, objLive);
    }

    if (traceRef.current) {
      console.log(`MyBets: useStatoScommessaTabs.tabsStatoScommessa: `, result.map((x) => x.id).join(','));
    }

    return result;
  }, [t, tipoScommessa]);

  const statoScommessaIndex = useMemo(
    () => tabsStatoScommessa.findIndex((x) => x.id === statoScommessa),
    [tabsStatoScommessa, statoScommessa]
  );

  const setStatoScommessaByIndex = async (idx: number): Promise<void> => {
    const nextValue = tabsStatoScommessa?.[idx]?.id;
    if (traceRef.current) {
      console.log(`MyBets: useStatoScommessaTabs.setStatoScommessaByIndex(${idx}) -> ${nextValue}`);
    }
    await setStatoScommessa(nextValue as MovimentiStatoScommessaEnum);
  };

  // if(traceRef.current){
  //   console.log(`MyBets: useStatoScommessaTabs statoScommessa:${statoScommessa}, statoScommessaIndex:${statoScommessaIndex}, tabsStatoScommessa:`, tabsStatoScommessa)
  // }
  return {
    statoScommessa,
    statoScommessaIndex,
    tabsStatoScommessa,
    setStatoScommessa,
    setStatoScommessaByIndex,
  };
};

const oneMin = 1000 * 60;
const fiveMin = oneMin * 5;

const SnaiItemFromHpybetModel = (item: MovimentiHbetMovimentoResDto): ScommesseResponseApiItem => ({
  stato: item.stato,
  importo: item.importo,
  vincita: item.vincita,
  tipologia: item.tipologia,
  hasCashout: isTruthy(item.hasCashout),
  idBiglietto: item.idBiglietto,
  dataModifica: item.dataModifica,
  dataEmissione: item.dataMovimento,
  importoCashout: item.importoCashout,
  codiceTipoGioco: item.codiceTipoGioco,
  vincitaPotenziale: item.vincitaPotenziale,
});

interface MyBetsState {
  isLoading: boolean;
  totalPages: number;
  cashOutOnly: boolean;
  currentPage: number;
}

export interface MyBetsProps {
  pageSize: number;
}

export interface MyBetsType {
  data: Array<ScommesseResponseApiItem>;
  isError: boolean;
  isLoading: boolean;
  totalPages: number;
  currentPage: number;
  cashOutOnly: boolean;
  doFetch: () => void;
  setCurrentPage: (_: number) => void;
  setCashOutOnly: (_: boolean) => void;
}
export const useMyBets = ({ pageSize: size }: MyBetsProps): MyBetsType => {
  const pagedItems = size ?? 10;

  const tmr = useRef<NodeJS.Timeout>();
  const traceRef = useRef(false);
  useEffect(() => {
    if (isClientSide) {
      const search = new URLSearchParams(document.location.search.substring(1));
      const trace = search.get('trace') ?? '';
      traceRef.current = trace.toLowerCase().includes('bets');
    }
  }, []);

  const { asPath } = useRouter();
  const [scommessaTrigger, scommessaResult] = useLazyGetScommesseQuery();
  const [movimentiTrigger, movimentiResult] = useLazyGetMovimentiHappybetQuery();

  const { isAuthenticated } = useInternalSession();
  const isCarrelloOpen = useSelector(selectIsOpenCarrello);

  const selectedTab = useSelector(selectTab);
  const prevTab = useRef(CarrelloTabEnum.Biglietto);

  const tipoScommessa = useSelector(selectTipo);
  const prevTipo = useRef<MovimentiTipoScommessaEnum>(MovimentiTipoScommessaEnum.Tutto);

  const statoScommessa = useSelector(selectStato);
  const prevStato = useRef<MovimentiStatoScommessaEnum>(MovimentiStatoScommessaEnum.Tutto);

  const { dataDa, dataA } = useMemo(() => {
    const now = new Date();
    const weekBefore = subDays(now, 7);
    if (now.getMonth() - weekBefore.getMonth() > 1) {
      weekBefore.setDate(1);
      weekBefore.setMonth(now.getMonth() - 1);
    }

    const dataDa = format(weekBefore, 'yyyy-MM-dd');
    const dataA = format(now, 'yyyy-MM-dd');

    if (traceRef.current) {
      console.log(`MyBets: dataDa:${dataDa}, dataA:${dataA}`);
    }

    return { dataDa, dataA };
  }, []);

  const [state, setState] = useState<MyBetsState>({
    isLoading: false,
    totalPages: 1,
    cashOutOnly: false,
    currentPage: 1,
  });
  const { isLoading, totalPages, cashOutOnly, currentPage } = state;
  const prevCurrentPage = useRef(currentPage);
  const pagination = useMemo(
    () => ({
      pageNumber: 1,
      pageSize: pagedItems * currentPage,
    }),
    [currentPage, pagedItems]
  );

  const setCurrentPage = (currentPage: number) =>
    setState((prev) => {
      clearInterval(tmr.current);
      if (traceRef.current) {
        console.log(`MyBets: setCurrentPage ${prev.currentPage} -> ${currentPage}`);
      }
      return structuredClone({ ...prev, currentPage });
    });

  const setCashOutOnly = (cashOutOnly: boolean) =>
    setState((prev) => {
      clearInterval(tmr.current);
      if (traceRef.current) {
        console.log(`MyBets: setCashOutOnly ${prev.cashOutOnly} -> ${cashOutOnly}`);
      }
      return structuredClone({ ...prev, cashOutOnly });
    });

  const doReset = useCallback(() => {
    setState((prev) => {
      if (traceRef.current) {
        console.log(`MyBets: doReset`);
      }
      return structuredClone({
        ...prev,
        isLoading: true,
        totalPages: 1,
        currentPage: 1,
      });
    });
  }, []);

  // define api to call + parametes
  const { trigger, payload, interval } = useMemo(() => {
    const interval = statoScommessa === MovimentiStatoScommessaEnum.Chiusa ? fiveMin : oneMin;

    if (isHappybetSite && statoScommessa === MovimentiStatoScommessaEnum.Chiusa) {
      if (traceRef.current) {
        console.log(`MyBets: api - movimenti`);
      }

      return {
        trigger: movimentiTrigger,
        interval,
        payload: {
          stato: statoScommessa,
          direzione: MovimentiDirezioneEnum.Tutto,
          tipologia: MovimentiTipologiaEnum.Scommessa,
        },
      };
    }

    if (traceRef.current) {
      console.log(`MyBets: api - scommessa`);
    }
    return {
      trigger: scommessaTrigger,
      interval,
      payload: {
        stato: statoScommessa,
        tipologia: tipoScommessa,
        hasNoLimit: statoScommessa !== MovimentiStatoScommessaEnum.Chiusa,
      },
    };
  }, [statoScommessa, tipoScommessa, scommessaTrigger, movimentiTrigger]);

  // invokes the api + set intervall
  const doFetch = useCallback(
    (restart = false) => {
      // clearInterval(tmr.current);
      const curret = storage.getItem(uid);

      if (restart || curret === `${tmr.current}`) {
        if (restart) {
          clearInterval(tmr.current);
        }
        if (traceRef.current) {
          console.log(`MyBets: doFetch.start restart:${restart}, pagination:`, pagination);
        }
      } else if (curret !== `${tmr.current}`) {
        clearInterval(tmr.current);
        if (traceRef.current) {
          console.log(`MyBets: doFetch  ${curret} !== ${tmr.current} - kill`);
        }
        return;
      }

      trigger(
        structuredClone({
          ...payload,
          dataA,
          dataDa,
          pagination,
        }) as any
      );

      if (restart) {
        const timeout = setInterval(doFetch, interval);
        if (traceRef.current) {
          console.log(`MyBets.onChange tmr:${tmr.current} -> ${timeout}`);
        }
        storage.setItem(uid, `${timeout}`);
        tmr.current = timeout;
      }
    },
    [trigger, payload, dataA, dataDa, pagination, interval]
  );

  // detect to stop interval or reset/continue
  useEffect(() => {
    clearInterval(tmr.current);

    let reset = true;
    let trigger = false;

    if (isAuthenticated && (isPersonalUrl(asPath) || (isCarrelloOpen && selectedTab === CarrelloTabEnum.Scommesse))) {
      reset = false;
      trigger = true;
      if (prevTab.current !== selectedTab) {
        if (traceRef.current) {
          console.log(`MyBets: tab.changed ${prevTab.current} -> ${selectedTab}`);
        }
        reset = true;
        prevTab.current = selectedTab;
      }

      if (prevTipo.current !== tipoScommessa) {
        if (traceRef.current) {
          console.log(`MyBets: tipo.changed ${prevTipo.current} -> ${tipoScommessa}`);
        }
        reset = true;
        prevTipo.current = tipoScommessa;
      }

      if (prevStato.current !== statoScommessa) {
        if (traceRef.current) {
          console.log(`MyBets: stato.changed ${prevStato.current} -> ${statoScommessa}`);
        }
        reset = true;
        prevStato.current = statoScommessa;
      }

      if (prevCurrentPage.current !== currentPage) {
        if (traceRef.current) {
          console.log(`MyBets: page.changed ${prevCurrentPage.current} -> ${currentPage}`);
        }
        prevCurrentPage.current = currentPage;
      }
    }

    if (reset) {
      doReset();
    }

    if (trigger) {
      doFetch(true);
    } else {
      if (traceRef.current) {
        console.log(`MyBets.onChange tmr:${tmr.current} - stop`);
      }
      storage.removeItem(uid);
    }

    return () => {
      storage.removeItem(uid);
      clearInterval(tmr.current);
    };
  }, [
    asPath,
    isAuthenticated,
    selectedTab,
    statoScommessa,
    tipoScommessa,
    isCarrelloOpen,
    currentPage,
    interval,
    doFetch,
    doReset,
  ]);

  // decodes result data
  const { raw, isError } = useMemo(() => {
    if (isHappybetSite && statoScommessa === MovimentiStatoScommessaEnum.Chiusa) {
      const { data, isError, isLoading, isFetching } = movimentiResult ?? {};
      if (!(isLoading || isFetching)) {
        setState((prev) => {
          if (traceRef.current) {
            console.log(`MyBets: loading Movimenti query result`);
          }
          return structuredClone({
            ...prev,
            isLoading: false,
            totalPages: Math.ceil((data?.totalCount ?? 0) / pagedItems),
          });
        });
      }

      return {
        raw: data?.items?.map(SnaiItemFromHpybetModel),
        isError,
      };
    }

    const { data, isError, isLoading, isFetching } = scommessaResult ?? {};

    const raw = (data?.items ?? [])?.filter(({ hasCashout }) => (isTruthy(cashOutOnly) ? isTruthy(hasCashout) : true));

    if (!(isLoading || isFetching)) {
      setState((prev) => {
        if (traceRef.current) {
          console.log(`MyBets: loading Scommessa query result`);
        }

        return structuredClone({
          ...prev,
          isLoading: false,
          totalPages: Math.ceil((data?.totalCount ?? 0) / pagedItems),
        });
      });
    }
    return { raw, isError };
  }, [cashOutOnly, movimentiResult, pagedItems, scommessaResult, statoScommessa]);

  // compose pagination
  const data = useMemo(
    () => (raw ?? [])?.filter((_, idx) => idx < pagedItems * currentPage),
    [currentPage, pagedItems, raw]
  );

  useEffect(() => {
    return () => {
      storage.removeItem(uid);
      clearInterval(tmr.current);
    };
  }, []);
  // if(traceRef.current){
  //   console.log(`MyBets: isAuthenticated:${isAuthenticated}, isCarrelloOpen:${isCarrelloOpen}, selectedTab:${selectedTab}, tipoScommessa:${tipoScommessa}, statoScommessa:${statoScommessa}`)
  // }
  return {
    data,
    isError,
    isLoading,
    totalPages,
    currentPage,
    cashOutOnly,
    doFetch,
    setCurrentPage,
    setCashOutOnly,
  };
};
